import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import TeamBadge from '../TeamBadge/TeamBadge';
import { DASHBOARD_STACK } from '../../utils/screenNames';
import { computeTournamentStatus } from '../../utils/tournamentStatus';

const TeamCell = ({ team }) => (
  <div className="flex flex-col items-center gap-2 w-[96px]">
    <TeamBadge
      src={team.crest}
      alt={team.title}
      containerSize="w-12 h-12"
      size="w-4 h-4"
    />
    <p className="text-app-text text-[12px] font-medium text-center leading-tight m-0 break-words w-full line-clamp-2">
      {team.title}
    </p>
  </div>
);

const CategoryCard = ({ category, showHeader }) => (
  <div className="bg-app-surface rounded-2xl p-4 md:p-5 flex flex-col gap-3">
    {showHeader && category.categoryName && (
      <p className="text-app-text-secondary text-[13px] font-medium m-0">
        {category.categoryName}
      </p>
    )}
    <div className="flex flex-wrap items-start gap-x-4 gap-y-5">
      {category.knockoutProgressingTeams.length === 0 ? (
        <p className="text-app-text-secondary text-[13px] m-0">
          No teams progressing yet.
        </p>
      ) : (
        category.knockoutProgressingTeams.map((team) => (
          <TeamCell key={team.teamId} team={team} />
        ))
      )}
    </div>
  </div>
);

const DashboardKnockoutProgressing = () => {
  const navigate = useNavigate();
  const { selectedTournament, dashboard } = useSelector((state) => state.tournament);
  const tournamentId = selectedTournament?.id || selectedTournament?._id;
  const isFresh = dashboard && dashboard.tournamentId === tournamentId;
  const status = useMemo(
    () => computeTournamentStatus(selectedTournament),
    [selectedTournament]
  );

  // Categories that actually have group→knockout progression configured. A
  // null `knockoutProgressingTeams` means the category isn't a group→knockout
  // format; we drop it from the list entirely. Categories with an empty array
  // (rules exist but nothing has resolved yet) still appear with a placeholder.
  const groupedCategories = useMemo(() => {
    if (!isFresh) return [];
    return (dashboard.categories || []).filter(
      (c) => Array.isArray(c.knockoutProgressingTeams)
    );
  }, [isFresh, dashboard]);

  // Only relevant while the tournament is mid-flight — pre-tournament these
  // teams aren't actually progressing yet, post-tournament use Standings.
  if (status !== 'IN_PROGRESS') return null;

  // Hide entirely when no category has group→knockout progression configured.
  if (groupedCategories.length === 0) return null;

  const isMulti = groupedCategories.length > 1;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-app-text text-[18px] font-medium m-0">
          Teams currently progressing to Knockout Stage
        </h3>
        <button
          type="button"
          onClick={() => navigate(DASHBOARD_STACK.STANDINGS)}
          className="text-app-text-secondary text-[13px] underline hover:text-app-text transition-colors cursor-pointer"
        >
          See more
        </button>
      </div>

      {isMulti ? (
        // Multiple categories → one card per category, laid out horizontally.
        // auto-fit lets cards split available width evenly, wrap when narrow.
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
        >
          {groupedCategories.map((cat) => (
            <CategoryCard key={cat.categoryId} category={cat} showHeader />
          ))}
        </div>
      ) : (
        // Single category → one full-width card with no category sub-heading.
        <CategoryCard category={groupedCategories[0]} showHeader={false} />
      )}
    </div>
  );
};

export default DashboardKnockoutProgressing;
